import { Injectable, Injector, Scopes } from '@visiba/instantiation';
import { Presentation } from '@visiba/presentation';
import { delay, Timer } from '@visiba/std';
import { DialogAlertSuccessError, toast } from '@cellula/react';
import { Issues, CaseModelShared } from './case_model_shared';
import { DateDisplayFormats, DateService } from '@application/time/date.service';
import { RouterService } from '@cortex-shim/router.service';
import { FormManager } from '@feature/dynamic_form/form_manager/mod';
import { UnhandledIssuesService } from '@feature/issues/unhandled_issues.service';
import { withTranslation } from '@application/i18n/with_translation';
import { FileWorkerStale } from '@feature/file_manager/file_worker/file_worker_stale';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { CounterState, createCounterState } from '@application/counter';
import { CasesApiService } from '@api_patient/cases_api.service';
import { AppointmentApiService } from '@api_patient/appointment_api.service';
import { CaseCommunicator } from './_case_communicator';
import { States } from '@api_patient/generated/clientv2/models/states';
import type { CaseOtherApiModel } from '@api_patient/generated/clientv2/models';
import type { PaymentApiModel, CurrencyDisplay } from '@api_patient/generated/clientv2/models';
import type { Participant, AppointmentType } from './common';
import type { AttemptData } from '@feature/payment/payment.controller';
import { UnitService } from '@application/unit.service';
import { PhysicalLocationApiService } from '@api_patient/physical_location_api.service';

interface Representative {
	for: string;
	name: string;
}

export interface AppointmentData {
	readonly ticket: string;
	readonly representative: Readonly<Representative> | null;
	readonly cancelable: boolean;
	readonly completed: boolean;
	readonly lobbyIsOpen: boolean;
	readonly date: VisibaDate;
	readonly reservedUntil: CounterState | null;
	readonly participants: Readonly<Participant>[];
	readonly receptionName: string;
	readonly rfv: Nullable<string>;
	readonly type: AppointmentType;
	readonly issues: Issues;
	readonly link: string | null;
}

interface PhysicalLocation {
	address: string;
	address2: string;
	postalCode: string;
	city: string;
	country: string;
	locationName: string;
	locationDescription: string;
	imageUrl: string | null;
}

export interface MetaData {
	readonly bookingId: number;
	readonly files: FileWorkerStale[];
	readonly forms: FormManager;
	readonly rfv: string;
	readonly reason: string;
	readonly day: string;
	readonly time: string;
	readonly physicalLocation: PhysicalLocation | null;
	readonly payment: PaymentApiModel | null;
	readonly price: CurrencyDisplay | null;
	readonly vonageEncryptionRequired: boolean;
}

interface Presentation {
	readonly data: AppointmentData | null;
	readonly metaData: MetaData | null;
}

@Injectable({
	scope: Scopes.Transient,
})
export class AppointmentCaseModel extends CaseModelShared<AppointmentData> {
	public static readonly APPOINTMENT_UNHANDLED_ISSUES_TRACKING_ID_PREFIX = 'A_';
	public readonly presentation = Presentation.create<Presentation>({
		data: null,
		metaData: null,
	});

	constructor(
		private readonly dateService: DateService,
		private readonly appointmentApiService: AppointmentApiService,
		private readonly physicalLocationApiService: PhysicalLocationApiService,
		private readonly routerService: RouterService,
		private readonly injector: Injector,
		private readonly unhandledIssuesService: UnhandledIssuesService,
		private readonly casesApiService: CasesApiService,
		private readonly caseCommunicator: CaseCommunicator,
		private readonly analyticService: AnalyticService,
		private readonly unitService: UnitService,
	) {
		super();
	}

	public updateLesserData(caseData: CaseOtherApiModel): void {
		if (caseData.AppointmentTicket == null) throw new Error('Invalid appointment');

		const participants: Participant[] = [];
		for (const participant of caseData.OtherParticipants) {
			participants.push({
				descriptiveTitle: participant.DescriptiveTitle,
				title: participant.Title,
				leftTimestamp: participant.LeftTimestamp,
				// TODO PAT-426: Allow null?
				name: participant.Name ?? '',
				profileImageUrl: participant.ProfileImageUrl,
			});
		}

		let type: AppointmentType;
		switch (caseData.Type) {
			case 'Appointment': {
				type = 'video';
				break;
			}
			case 'PhysicalAppointment': {
				type = 'physical';
				break;
			}
			default: {
				throw new Error('Unimplemented type');
			}
		}

		// const unit = this.unitService.getUnitNodeInTree(caseData.UnitId);

		let representative: Representative | null = null;
		if (caseData.AsAgentFor) {
			representative = {
				for: caseData.AsAgentFor,
				name: caseData.AsAgentForName ?? '',
			};
		}

		const unit = this.unitService.getUnitNodeInTree(caseData.UnitId);
		const route = '/' + unit.routeWithoutLicenceholder + '/appointment/' + caseData.AppointmentTicket;

		const link = caseData.Type === 'Appointment' ? route : null;

		let reservedUntil: CounterState | null = null;
		if (caseData.Reserved && caseData.ReservedUntil != null) {
			reservedUntil = createCounterState(this.dateService.newDate(caseData.ReservedUntil));
		}

		const startDate = this.dateService.newDate(caseData.Time);
		const now = this.dateService.newDate();

		// Lobby opens 15 minutes before the start time
		const lobbyIsOpen = now.isBetween(startDate.subtract(15, 'm'), startDate.endOf('day'));

		this.presentation.write({
			data: {
				type,
				link,
				representative,
				reservedUntil,
				lobbyIsOpen,
				cancelable: caseData.Cancelable,
				ticket: caseData.AppointmentTicket,
				completed: caseData.Completed,
				date: this.dateService.create(caseData.Time),
				participants: participants,
				receptionName: caseData.ReceptionName ?? '',
				issues: this.parseIssues(caseData),
				rfv: caseData.Condition,
			},
		});
	}

	public async updateExtendedData() {
		const result = await this.appointmentApiService.get(this.getIdentifier());
		// TODO: handle error
		if (result.err) return;

		const data = result.ok.data;
		let physicalLocation: PhysicalLocation | null = null;

		if (data.PhysicalLocationId != null) {
			const physicalLocationResponse = await this.physicalLocationApiService.getPhysicalLocation(
				data.PhysicalLocationId,
				data.ReceptionId,
			);

			if (physicalLocationResponse.ok) {
				const location = physicalLocationResponse.ok.data;
				physicalLocation = {
					address: location.Address ?? '',
					address2: location.Address2 ?? '',
					postalCode: location.PostalCode ?? '',
					city: location.City ?? '',
					country: location.Country ?? '',
					locationName: location.Name ?? '',
					locationDescription: location.Description ?? '',
					imageUrl: location.ImageUrl,
				};
			}
		}

		const startDate = this.dateService.create(data.Start);
		const endDate = startDate.add(data.Duration, 'minutes');
		const day = startDate.format(
			this.dateService.getLocaleSpecificDateFormatTemplate(DateDisplayFormats.FULL_DISPLAY_NO_TIME_WEEKDAY),
		);
		const start = startDate.format('LT');
		const end = endDate.format('LT');

		const files = data.Files.map(
			(file) =>
				new FileWorkerStale({
					downloadUrl: file.DownloadUrl ?? '',
					fileName: file.Name,
					fileSize: file.Length,
					fileType: file.Type,
					thumbnailUrl: file.ThumbnailUrl ?? undefined,
				}),
		);

		const formHandler = this.injector.resolve(FormManager);
		formHandler.addRespondants(
			data.Forms.map((form) => ({
				completed: form.Completed,
				formId: form.FormId,
				header: form.Header ?? '',
				opportunity: form.Opportunity,
				respondantId: form.RespondantId,
				issueTrackingId: AppointmentCaseModel.APPOINTMENT_UNHANDLED_ISSUES_TRACKING_ID_PREFIX + this.getIdentifier(),
				dataProvider: {
					type: 'respondant',
					respondantId: form.RespondantId,
				},
			})),
		);

		this.presentation.write({
			metaData: {
				day,
				files,
				bookingId: data.BookingId,
				forms: formHandler,
				reason: data.Reason ?? '',
				rfv: data.Condition?.Name ?? '',
				time: `${start} - ${end} (${data.Duration} ${withTranslation('Common.Word.Minutes').toLocaleLowerCase()})`,
				physicalLocation: physicalLocation,
				payment: data?.Payment ?? null,
				price: data?.Price ?? null,
				vonageEncryptionRequired: data?.VonageEncryptionRequired ?? false,
			},
		});
	}

	public markAsSeen(): void {
		const data = this.get();

		this.casesApiService.deleteNewIncoming(data.ticket).catch(() => {
			// noop
		});

		this.unhandledIssuesService.removeArbitraryIssue(
			AppointmentCaseModel.APPOINTMENT_UNHANDLED_ISSUES_TRACKING_ID_PREFIX + this.getIdentifier(),
			['isNewCase'],
		);
	}

	public markAsPayed(data: AttemptData): void {
		const prev = this.presentation.read();
		if (prev.data == null) throw new Error('did not expect data to be null here');
		if (prev.metaData == null) throw new Error('did not expect metaData to be null here');
		if (prev.metaData.payment == null) throw new Error('did not expect payment field to be null here');

		this.presentation.write({
			data: {
				...prev.data,
				reservedUntil: null,
			},
			metaData: {
				...prev.metaData,
				payment: {
					...prev.metaData.payment,
					ReceiptUrl: data.receiptUrl as string,
					State: States.Paid,
				},
			},
		});
	}

	public get(): AppointmentData {
		const data = this.presentation.read().data;
		if (data == null) throw new Error('Did not expect data to be null');

		return data;
	}

	public getIdentifier(): string {
		const data = this.presentation.read().data;
		if (data?.ticket == null) throw new Error('data not set');

		return data.ticket;
	}

	public getBookingId(): number {
		const metaData = this.presentation.read().metaData;
		if (metaData == null) throw new Error('metaData not set');

		return metaData.bookingId;
	}

	public remove(): void {
		this.caseCommunicator.removeAppointmentCase(this.getIdentifier());
		this.unhandledIssuesService.removeIssuesByTrackingId(
			AppointmentCaseModel.APPOINTMENT_UNHANDLED_ISSUES_TRACKING_ID_PREFIX + this.getIdentifier(),
		);
	}

	public async cancelAppointment(): Promise<void> {
		try {
			await delay(250);

			const { participants } = this.get();
			if (participants.length > 1) {
				await this.appointmentApiService.decline(this.getBookingId());
			} else {
				await this.appointmentApiService.cancel(this.getIdentifier());
			}

			this.remove();

			this.routerService.navigateByUrl('/visits');

			Timer.wait(() => {
				toast({
					label: withTranslation('PW.Component.Visits.AppointmentInfo.CancelCall.SuccessToast'),
					variant: 'success',
				});
			}, 300);
		} catch {
			throw new DialogAlertSuccessError(withTranslation('PW.Component.Visits.AppointmentInfo.CancelCall.Error'));
		}
	}

	public analyticQuirk_trackOnMountInVisitsView(): void {
		const data = this.get();

		this.analyticService.trackEvent(
			AnalyticCategories.Visits,
			`Opened: ${data.type === 'physical' ? 'Physical' : 'Video'} appointment (${data.completed ? 'completed' : 'ongoing'})`,
		);
	}
}
